import { apiGet, apiPut, apiPost, apiDelete } from './api';

interface UserData {
  id: number;
  username: string;
  email: string;
  profilePhoto: string | null;
  birthdate: string;
  gender: string;
  bio: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  suspendedAt: string | null;
  lockedUntil?: string | null;
  failedLoginAttempts?: number;
  createdAt: string;
  updatedAt: string;
  _count: {
    ownedGroups: number;
    groupMemberships: number;
    messages: number;
    createdEvents: number;
  };
}

interface UsersResponse {
  users: UserData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class AdminUserService {
  /**
   * Get all users with filtering and pagination
   */
  async getAllUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    suspended?: boolean;
    isAdmin?: boolean;
    sortBy?: string;
    sortOrder?: string;
  } = {}): Promise<UsersResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    return apiGet<UsersResponse>(`/admin/users?${searchParams.toString()}`);
  }

  /**
   * Suspend or unsuspend a user
   */
  async suspendUser(userId: number, suspend: boolean): Promise<UserData> {
    return apiPut<UserData>(`/admin/users/${userId}/suspend`, { suspend });
  }

  async unlockUser(userId: number): Promise<UserData> {
    return apiPost<UserData>(`/admin/users/${userId}/unlock`, {});
  }

  /**
   * Get detailed user profile for admin
   */
  async getUserProfile(userId: string) {
    return apiGet(`/admin/users/${userId}`);
  }

  /**
   * Update user profile (admin only)
   */
  async updateUser(userId: string, updates: {
    username?: string;
    email?: string;
    isAdmin?: boolean;
    bio?: string | null;
    extraGroupSlots?: boolean;
    chatEnhancements?: boolean;
  }) {
    return apiPut(`/admin/users/${userId}`, updates);
  }

  /**
   * Delete a user completely
   */
  async deleteUser(userId: number): Promise<void> {
    return apiDelete<void>(`/admin/users/${userId}`);
  }
}

export const adminUserService = new AdminUserService();